import type { AppMenuConfig, AppMenuItem } from '@workspace/ui';

export interface FeaturePruneOptions {
  apiGatewayFeatureEnabled: boolean;
  commerceFeatureEnabled: boolean;
  mappingAccessEnabled: boolean;
  notificationsAccessEnabled: boolean;
  paymentsAccessEnabled?: boolean;
  resellerFeatureEnabled: boolean;
  statusAccessEnabled: boolean;
  threatProtectionAccessEnabled?: boolean;
  salesAccessEnabled: boolean;
  tenantUsersAccessEnabled: boolean;
}

const API_GATEWAY_IDS = new Set(['api-gateway']);
const COMMERCE_IDS = new Set(['commerce']);
const MAPPING_IDS = new Set(['maps']);
const NOTIFICATION_IDS = new Set(['notifications']);
const PAYMENT_IDS = new Set(['payments']);
const RESELLER_IDS = new Set(['reseller']);
const STATUS_IDS = new Set(['status']);
const SALES_IDS = new Set(['sales.map', 'sales.leads']);
const TENANT_USERS_IDS = new Set(['users']);

export function pruneMenuConfig(menu: AppMenuConfig, options: FeaturePruneOptions): AppMenuConfig {
  return {
    ...menu,
    sideMenu: pruneItems(menu.sideMenu, options),
    topMenu: pruneItems(menu.topMenu, options),
    userMenu: pruneItems(menu.userMenu, options)
  };
}

function pruneItems(items: AppMenuItem[] = [], options: FeaturePruneOptions): AppMenuItem[] {
  return items
    .map(item => {
      if (options.threatProtectionAccessEnabled === false && (item.id === 'threat-protection' || item.id?.startsWith('threat-protection.'))) return null;
      if (!options.apiGatewayFeatureEnabled && shouldHideApiGatewayItem(item.id)) {
        return null;
      }
      if (!options.commerceFeatureEnabled && shouldHideCommerceItem(item.id)) {
        return null;
      }
      if (!options.salesAccessEnabled && shouldHideSalesItem(item.id)) {
        return null;
      }
      if (!options.mappingAccessEnabled && shouldHideMappingItem(item.id)) {
        return null;
      }
      if (!options.notificationsAccessEnabled && shouldHideNotificationsItem(item.id)) {
        return null;
      }
      if (options.paymentsAccessEnabled === false && shouldHidePaymentsItem(item.id)) {
        return null;
      }
      if (!options.resellerFeatureEnabled && shouldHideResellerItem(item.id)) {
        return null;
      }
      if (!options.statusAccessEnabled && shouldHideStatusItem(item.id)) {
        return null;
      }
      if (!options.tenantUsersAccessEnabled && shouldHideTenantUsersItem(item.id)) {
        return null;
      }
      const children = item.children ? pruneItems(item.children, options) : undefined;
      return { ...item, children };
    })
    .filter((item): item is AppMenuItem => Boolean(item));
}

function shouldHideApiGatewayItem(id?: string): boolean {
  if (!id) {
    return false;
  }
  return API_GATEWAY_IDS.has(id) || id.startsWith('api-gateway.');
}

function shouldHideMappingItem(id?: string): boolean {
  if (!id) {
    return false;
  }
  return MAPPING_IDS.has(id) || id.startsWith('maps.');
}

function shouldHideCommerceItem(id?: string): boolean {
  if (!id) {
    return false;
  }
  return COMMERCE_IDS.has(id) || id.startsWith('commerce.');
}

function shouldHideNotificationsItem(id?: string): boolean {
  if (!id) {
    return false;
  }
  return NOTIFICATION_IDS.has(id) || id.startsWith('notifications.');
}

function shouldHidePaymentsItem(id?: string): boolean {
  return Boolean(id && (PAYMENT_IDS.has(id) || id.startsWith('payments.')));
}

function shouldHideResellerItem(id?: string): boolean {
  if (!id) {
    return false;
  }
  return RESELLER_IDS.has(id) || id.startsWith('reseller.');
}

function shouldHideStatusItem(id?: string): boolean {
  if (!id) {
    return false;
  }
  return STATUS_IDS.has(id) || id.startsWith('status.');
}

function shouldHideSalesItem(id?: string): boolean {
  if (!id) {
    return false;
  }
  return SALES_IDS.has(id) || id.startsWith('sales.');
}

function shouldHideTenantUsersItem(id?: string): boolean {
  if (!id) {
    return false;
  }
  return TENANT_USERS_IDS.has(id) || id.startsWith('users.');
}
